import React, { useState } from 'react';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';

import { Control, Controller } from 'react-hook-form';
import { cn } from '@client/lib';

function SliderTooltip({
  isHovered,
  value,
  position,
  isDefault = false,
  suffix = '',
}: {
  isHovered: boolean;
  value: number | string;
  position: number; // 0-100 percentage
  isDefault?: boolean;
  suffix?: string;
}) {
  // Adjust position to ensure tooltip doesn't go outside container on either side
  // Minimum 8% from left, maximum 92% from left (8% from right)
  const adjustedPosition = Math.min(Math.max(position, 8), 92);
  return (
    <div
      className={`pointer-events-none absolute z-50 -mt-10 -translate-x-1/2 transform whitespace-nowrap rounded px-2 py-1 text-xs text-white shadow-md transition-opacity ${
        isHovered ? 'opacity-100' : 'opacity-0'
      } ${isDefault ? 'bg-primary' : 'bg-primary-dark'}`}
      style={{
        left: `${adjustedPosition}%`,
      }}
    >
      <span className="font-semibold">
        {value}
        {suffix}
      </span>
    </div>
  );
}

interface CustomSliderProps {
  'name': string;
  'control': Control<any>;
  'min': number;
  'max': number;
  'step'?: number;
  'defaultValue': number;
  'formatValue'?: (value: number) => string | number;
  'updateClient'?: (value: number) => void;
  'labelSuffix'?: string;
  'defaultLabel'?: string;
  'hideResetButton'?: boolean;
  'label'?: string;
  'data-testid'?: string;
}

export function CustomSlider({
  name,
  control,
  min,
  max,
  step = 1,
  defaultValue,
  formatValue,
  updateClient,
  labelSuffix = '',
  defaultLabel = 'Default',
  hideResetButton = false,
  label,
  'data-testid': testId,
}: CustomSliderProps) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange } }) => {
        const isDefault = value === defaultValue;

        const handleReset = () => {
          onChange(defaultValue);
          if (updateClient) {
            updateClient(defaultValue);
          }
        };

        return (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              {label && (
                <div className="text-primary-dark text-lg font-semibold">{label}</div>
              )}
              <Button
                onClick={handleReset}
                variant="outline"
                size="sm"
                className={cn('border-primary/25 text-primary-dark ml-auto h-7 px-2 text-xs', {
                  'invisible': isDefault || hideResetButton,
                })}
              >
                {defaultLabel}
              </Button>
            </div>
            <div className="relative">
              <Slider
                value={[value]}
                onValueChange={vals => {
                  onChange(vals[0]);
                  if (updateClient) {
                    updateClient(vals[0]);
                  }
                }}
                min={min}
                max={max}
                step={step}
                className="[&_[data-slot=slider-thumb]]:size-4 [&_[data-slot=slider-track]]:h-1.5"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                data-testid={testId}
              />

              <SliderTooltip
                isHovered={isHovered}
                value={formatValue ? formatValue(value) : value}
                position={((value - min) / (max - min)) * 100}
                isDefault={isDefault}
                suffix={labelSuffix}
              />
            </div>
          </div>
        );
      }}
    />
  );
}